import { useSyncExternalStore } from 'react';
import { THEME_COLOR } from '@mockd/shared/brand';
import {
  applyTheme,
  parseThemePreference,
  THEME_STORAGE_KEY,
  type ThemePreference,
} from '../theme/initScript';

/*
 * localStorage is the store; the inline head script (vite.config.ts) has already applied
 * it before React loads, so this only has to read it, write it, and re-apply on change.
 */

const listeners = new Set<() => void>();

const emit = () => listeners.forEach(listener => listener());

function readTheme(): ThemePreference {
  try {
    return parseThemePreference(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return 'system';
  }
}

/** Another tab changed the preference. `key` is null when it cleared all of storage. */
function onStorage(event: StorageEvent) {
  if (event.key !== null && event.key !== THEME_STORAGE_KEY) return;
  applyTheme(readTheme(), THEME_COLOR);
  emit();
}

function subscribe(listener: () => void) {
  if (listeners.size === 0) window.addEventListener('storage', onStorage);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener('storage', onStorage);
  };
}

function setTheme(theme: ThemePreference) {
  try {
    if (theme === 'system') localStorage.removeItem(THEME_STORAGE_KEY);
    else localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Storage unavailable: the choice still applies to this page, it just won't persist.
  }
  applyTheme(theme, THEME_COLOR);
  emit();
}

export function useTheme() {
  // Prerendering has no storage, so the server snapshot is System; hydration starts
  // there and then re-renders with the stored value instead of mismatching.
  const theme = useSyncExternalStore(subscribe, readTheme, () => 'system' as const);
  return { theme, setTheme };
}
